import React from 'react';
import FieldIcon from 'components/Icons/FieldIcon';
import NameListTooltip from 'components/NameListTooltip';
import cleanGeometry from 'helpers/cleanGeometry';
import { Geometry } from 'geojson';

interface FieldsDisplayProps {
  fields: Array<{
    id: string;
    name: string;
  }>;
  firstFieldInfo: {
    id?: string;
    name?: string;
    geometry?: Geometry;
  };
  totalFields: number;
  showSubheader?: boolean;
  iconWidth?: number;
  iconHeight?: number;
  iconColor?: string;
  iconBorderColor?: string;
  disablePositionOffset?: boolean;
  isSelected?: boolean;
  maxTooltipLines?: number;
  maxTooltipItems?: number;
  tooltipPlacement?: 'topRight' | 'bottomRight';
  tooltipNameWrap?: 'characters' | 'words';
  spacing?: 'default' | 'compact';
}

const FieldsDisplay: React.FC<FieldsDisplayProps> = ({
  fields,
  firstFieldInfo,
  totalFields,
  showSubheader = false,
  iconWidth,
  iconHeight,
  iconColor = '#DFE2E7',
  iconBorderColor = '#868CA2',
  disablePositionOffset = false,
  isSelected = false,
  maxTooltipLines = 15,
  maxTooltipItems,
  tooltipPlacement = 'topRight',
  tooltipNameWrap = 'characters',
  spacing = 'default'
}) => {
  const safeFields = fields || [];
  const firstField = safeFields[0];
  const fieldGeometry = cleanGeometry(firstFieldInfo?.geometry);
  const safeTotalFields = totalFields || 0;

  if (!firstField) {
    return (
      <div className="text-left">
        {showSubheader && (
          <div className="text-xs font-normal" style={{ color: '#696F88' }}>
            Fields
          </div>
        )}
        <div className="text-sm text-gray-900">No fields</div>
      </div>
    );
  }

  const SUBTITLE_COLOR = '#696F88';

  const displayContent = (
    <div className="min-w-0">
      {/* Optional subheader */}
      {showSubheader && (
        <div className="text-xs font-normal" style={{ color: SUBTITLE_COLOR }}>
          Fields
        </div>
      )}
      {/* Field display */}
      <div
        className={`flex items-center min-w-0 ${
          spacing === 'compact' ? 'gap-1' : 'gap-2'
        }`}
        style={
          disablePositionOffset
            ? undefined
            : { position: 'relative', top: '-7px' }
        }
      >
        {/* The glyph is a rendered field outline, so it needs real geometry.
            Skipped when there is none rather than passed an empty placeholder:
            renderFeaturesToSvg throws "line is required" on `{type: '',
            coordinates: []}`, which took down the whole row. Reachable from the
            Linked Records table, where firstFieldInfo is absent for a record
            with no fields. */}
        {fieldGeometry && (
          <FieldIcon
            id={firstFieldInfo?.name || 'unknown'}
            className="shrink-0"
            features={[
              {
                type: 'Feature',
                geometry: fieldGeometry,
                properties: {}
              }
            ]}
            border={iconBorderColor}
            color={iconColor}
            width={iconWidth}
            height={iconHeight}
          />
        )}
        <div className="text-sm text-gray-900 truncate break-all shrink min-w-0">
          {firstField?.name || 'No fields'}
        </div>

        {/* Chip + hover list. Shared with the Linked Records table so both
            behave identically — see components/NameListTooltip. */}
        <NameListTooltip
          title="Fields"
          names={safeFields}
          total={safeTotalFields}
          maxLines={maxTooltipLines}
          maxItems={maxTooltipItems}
          isSelected={isSelected}
          placement={tooltipPlacement}
          nameWrap={tooltipNameWrap}
        />
      </div>
    </div>
  );

  return displayContent;
};

export default FieldsDisplay;
